import { DEFAULT_QUOTATION_CLEANING_STYLE } from '@/utils/quotationDocument';

export const MAX_QUOTATION_CLEANING_STYLE_OPTIONS = 50;
export const MAX_QUOTATION_CLEANING_STYLE_LENGTH = 160;
export const MAX_QUOTATION_PROPERTY_OPTIONS = 50;
export const MAX_QUOTATION_PROPERTY_LENGTH = 500;
export const MAX_QUOTATION_SITE_ADDRESS_OPTIONS = 50;
export const MAX_QUOTATION_SITE_ADDRESS_LENGTH = 500;
export const MAX_QUOTATION_COST_ITEM_OPTIONS = 100;
export const MAX_QUOTATION_COST_ITEM_LENGTH = 160;

const normalizeQuotationTextOptions = (values, maxOptions, maxLength) => {
	if (!Array.isArray(values)) return [];

	const normalized = [];
	const seen = new Set();
	for (const value of values) {
		if (normalized.length >= maxOptions) break;
		const option = String(value ?? '')
			.trim()
			.slice(0, maxLength);
		const key = option.toLocaleLowerCase();
		if (!option || seen.has(key)) continue;
		seen.add(key);
		normalized.push(option);
	}
	return normalized;
};

export const normalizeQuotationCleaningStyleOptions = (values) =>
	normalizeQuotationTextOptions(
		values,
		MAX_QUOTATION_CLEANING_STYLE_OPTIONS,
		MAX_QUOTATION_CLEANING_STYLE_LENGTH,
	);

export const normalizeQuotationPropertyOptions = (values) =>
	normalizeQuotationTextOptions(
		values,
		MAX_QUOTATION_PROPERTY_OPTIONS,
		MAX_QUOTATION_PROPERTY_LENGTH,
	);

export const normalizeQuotationSiteAddressOptions = (values) =>
	normalizeQuotationTextOptions(
		values,
		MAX_QUOTATION_SITE_ADDRESS_OPTIONS,
		MAX_QUOTATION_SITE_ADDRESS_LENGTH,
	);

export const normalizeQuotationCostItemOptions = (values) =>
	normalizeQuotationTextOptions(
		values,
		MAX_QUOTATION_COST_ITEM_OPTIONS,
		MAX_QUOTATION_COST_ITEM_LENGTH,
	);

export const getQuotationCleaningStyleOptions = (documentPresets) => {
	const quotationSettings = documentPresets?.quotation;
	if (Array.isArray(quotationSettings?.cleaningStyleOptions)) {
		return normalizeQuotationCleaningStyleOptions(quotationSettings.cleaningStyleOptions);
	}
	return [DEFAULT_QUOTATION_CLEANING_STYLE];
};

export const getQuotationPropertyOptions = (documentPresets) => {
	const quotationSettings = documentPresets?.quotation;
	if (Array.isArray(quotationSettings?.propertyOptions)) {
		return normalizeQuotationPropertyOptions(quotationSettings.propertyOptions);
	}
	return [];
};

export const getQuotationSiteAddressOptions = (documentPresets) => {
	const quotationSettings = documentPresets?.quotation;
	if (Array.isArray(quotationSettings?.siteAddressOptions)) {
		return normalizeQuotationSiteAddressOptions(quotationSettings.siteAddressOptions);
	}
	return [];
};

export const getQuotationCostItemOptions = (documentPresets) => {
	const quotationSettings = documentPresets?.quotation;
	if (Array.isArray(quotationSettings?.costItemOptions)) {
		return normalizeQuotationCostItemOptions(quotationSettings.costItemOptions);
	}
	return [];
};

const mergeQuotationOptions = (documentPresets, key, values) => {
	const rootSettings =
		documentPresets && typeof documentPresets === 'object' && !Array.isArray(documentPresets)
			? documentPresets
			: {};
	const quotationSettings =
		rootSettings.quotation &&
		typeof rootSettings.quotation === 'object' &&
		!Array.isArray(rootSettings.quotation)
			? rootSettings.quotation
			: {};

	return {
		...rootSettings,
		version: 1,
		quotation: {
			...quotationSettings,
			[key]: values,
		},
	};
};

export const mergeQuotationCleaningStyleOptions = (documentPresets, values) =>
	mergeQuotationOptions(
		documentPresets,
		'cleaningStyleOptions',
		normalizeQuotationCleaningStyleOptions(values),
	);

export const mergeQuotationPropertyOptions = (documentPresets, values) =>
	mergeQuotationOptions(
		documentPresets,
		'propertyOptions',
		normalizeQuotationPropertyOptions(values),
	);

export const mergeQuotationSiteAddressOptions = (documentPresets, values) =>
	mergeQuotationOptions(
		documentPresets,
		'siteAddressOptions',
		normalizeQuotationSiteAddressOptions(values),
	);

export const mergeQuotationCostItemOptions = (documentPresets, values) =>
	mergeQuotationOptions(
		documentPresets,
		'costItemOptions',
		normalizeQuotationCostItemOptions(values),
	);
